import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import './PinGate.css';
import './AdminAuthGate.css';

export default function AdminAuthGate({ children }) {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [mode, setMode] = useState('account');
  const [pinAllowed, setPinAllowed] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const session = await api.getAdminMe();
        if (!cancelled && session) {
          setAdmin(session.admin || null);
          setAuthorized(true);
          sessionStorage.setItem('pin-auth-admin', 'true');
          return;
        }
      } catch {
        if (!cancelled) {
          sessionStorage.removeItem('pin-auth-admin');
          setAuthorized(false);
        }
      }

      try {
        const authMode = await api.getAdminAuthMode();
        if (!cancelled) {
          setPinAllowed(!!authMode.pinAllowed);
          if (authMode.preferred === 'pin' && authMode.pinAllowed && !authMode.accountsConfigured) {
            setMode('pin');
          } else {
            setMode('account');
          }
        }
      } catch {
        if (!cancelled) {
          setPinAllowed(false);
          setMode('account');
        }
      }
    })();

    return () => { cancelled = true; };
  }, []);

  async function handleAccountSubmit(e) {
    e.preventDefault();
    setError('');
    setChecking(true);
    try {
      const result = await api.adminLogin(email.trim(), password);
      sessionStorage.setItem('pin-auth-admin', 'true');
      if (result.token) api.setToken(result.token);
      setAdmin(result.admin || null);
      setAuthorized(true);
      setPassword('');
    } catch (err) {
      setError(err.message || 'Invalid email or password');
      setPassword('');
    } finally {
      setChecking(false);
    }
  }

  async function handlePinSubmit(e) {
    e.preventDefault();
    setError('');
    setChecking(true);
    try {
      const result = await api.verifyPin(pin, 'admin');
      if (result.valid) {
        sessionStorage.setItem('pin-auth-admin', 'true');
        if (result.token) api.setToken(result.token);
        setAdmin(null);
        setAuthorized(true);
      }
    } catch (err) {
      setError(err.message || 'Invalid PIN');
      setPin('');
    } finally {
      setChecking(false);
    }
  }

  if (authorized) {
    return typeof children === 'function' ? children({ admin }) : children;
  }

  return (
    <div className="pin-gate">
      <div className="pin-gate-card card admin-auth-card">
        <div className="pin-gate-icon">&#128274;</div>
        <h2 className="pin-gate-title">Admin Access</h2>
        <p className="pin-gate-desc">
          {mode === 'pin'
            ? 'Enter the admin PIN to continue'
            : 'Sign in with your admin account'}
        </p>

        {mode === 'account' ? (
          <form onSubmit={handleAccountSubmit} className="pin-gate-form admin-auth-form">
            <label className="admin-auth-label">
              Email
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                className="admin-auth-input"
                required
              />
            </label>
            <label className="admin-auth-label">
              Password
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="admin-auth-input"
                required
              />
            </label>
            {error && <p className="error-text">{error}</p>}
            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={checking || !email.trim() || !password}
            >
              {checking ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        ) : (
          <form onSubmit={handlePinSubmit} className="pin-gate-form">
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={8}
              placeholder="Enter PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              autoFocus
              className="pin-gate-input"
            />
            {error && <p className="error-text">{error}</p>}
            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={pin.length < 4 || checking}
            >
              {checking ? 'Checking…' : 'Enter'}
            </button>
          </form>
        )}

        {pinAllowed && (
          <button
            type="button"
            className="admin-auth-switch"
            onClick={() => {
              setError('');
              setMode((m) => (m === 'pin' ? 'account' : 'pin'));
            }}
          >
            {mode === 'pin' ? 'Use admin account instead' : 'Use PIN instead'}
          </button>
        )}

        <button
          type="button"
          className="pin-gate-back"
          onClick={() => navigate('/staff')}
        >
          ← Back to staff access
        </button>
      </div>
    </div>
  );
}
